/**
 * A chat message as the thread draws it: the wire record, read once into the facts every bubble
 * needs, plus what this browser knows about a send it made itself.
 *
 * ── Two sources, one row ─────────────────────────────────────────────────────────────────────
 * The server's messages live in the thread's query cache and nothing else writes there except the
 * server's own answers (a send's response, a `ReceiveMessage` broadcast). A send still on its way,
 * or one that failed, lives in `pendingSends` beside it, keyed by the `clientId` the send carries.
 * `mergeThread` lays the two together: a pending send whose `clientId` the server already returned
 * is dropped, so a message can never be drawn twice and never vanish between the two.
 *
 * ── `kind` is the discriminator, never the payload's presence ────────────────────────────────
 * `contentType` decides the bubble. A card type that arrived without its payload is `unsupported`
 * rather than a card drawn from nulls, and a type this build has no name for is the same — the
 * mobile app's `renderedBubbleKind`.
 */

import type {
  TrainWithMeModulesChatDtosResponsesMessageResponse as MessageResponse,
} from '~/shared/api/generated/model';

export type ChatMessageKind =
  | 'text'
  | 'image'
  | 'video'
  | 'voice'
  | 'exercise'
  | 'plan'
  | 'summary'
  | 'clipRequest'
  | 'clip'
  | 'unsupported';

export type DeliveryStatus = 'sending' | 'sent' | 'failed';

/** What a send this browser made carries until the server's record replaces it. */
export interface LocalSend {
  readonly status: 'sending' | 'failed';
  /** 0..1 while the bytes go up; `null` when nothing measurable is moving. */
  readonly progress: number | null;
  readonly stage: 'compressing' | 'sending' | 'processing' | null;
}

export interface ChatMessage {
  /** Stable across the hand-over from pending to server: the `clientId`. */
  readonly key: string;
  /** The server's id, or `null` while the send is pending. */
  readonly id: number | null;
  readonly clientId: string;
  readonly mine: boolean;
  readonly createdAt: string;
  readonly kind: ChatMessageKind;
  /** The text, or a media message's caption. A card's `content` is its snapshot title, not text. */
  readonly text: string;
  /** A line the app wrote itself (stored as the trainer's), drawn as a note rather than a bubble. */
  readonly system: boolean;
  readonly status: DeliveryStatus;
  readonly wire: MessageResponse;
  readonly local: LocalSend | null;
  /** An object URL of the file this browser picked — drawn until the server's own picture exists. */
  readonly previewUrl: string | null;
}

const KIND_BY_TYPE: Readonly<Record<string, ChatMessageKind>> = {
  Text: 'text',
  Image: 'image',
  Video: 'video',
  Voice: 'voice',
  ExerciseCard: 'exercise',
  TrainingPlan: 'plan',
  WorkoutSummary: 'summary',
  ExerciseClipRequest: 'clipRequest',
  ExerciseClip: 'clip',
};

export function messageKindOf(wire: MessageResponse): ChatMessageKind {
  const kind = KIND_BY_TYPE[wire.contentType ?? 'Text'] ?? 'unsupported';
  switch (kind) {
    case 'image':
      return wire.media ? kind : 'unsupported';
    case 'video':
      return wire.video ? kind : 'unsupported';
    case 'voice':
      return wire.voice ? kind : 'unsupported';
    case 'exercise':
      return wire.exerciseCard ? kind : 'unsupported';
    case 'plan':
      return wire.trainingPlan ? kind : 'unsupported';
    case 'summary':
      return wire.workoutSummary ? kind : 'unsupported';
    case 'clipRequest':
      return wire.exerciseClipRequest ? kind : 'unsupported';
    case 'clip':
      return wire.exerciseClip ? kind : 'unsupported';
    default:
      return kind;
  }
}

/** Whether `content` is words the sender wrote, rather than a card's snapshot title. */
export function carriesText(kind: ChatMessageKind): boolean {
  return kind === 'text' || kind === 'image' || kind === 'video' || kind === 'voice';
}

export function toChatMessage(
  wire: MessageResponse,
  myUserId: string | null,
  local: LocalSend | null,
  previewUrl: string | null = null,
): ChatMessage {
  const kind = messageKindOf(wire);
  const clientId = wire.clientId ?? `id-${String(wire.id ?? 0)}`;
  const id = typeof wire.id === 'number' && wire.id > 0 ? wire.id : null;
  const mine = myUserId !== null && wire.senderUserId === myUserId;
  return {
    key: clientId,
    id,
    clientId,
    mine,
    createdAt: wire.createdAt ?? new Date(0).toISOString(),
    kind,
    text: carriesText(kind) ? (wire.content ?? '').trim() : '',
    system: wire.isSystem === true && kind === 'text',
    status: local === null ? 'sent' : local.status,
    wire,
    local,
    previewUrl,
  };
}

/** Oldest first, ties by id, a pending send (no id) after every server row of its instant. */
export function compareMessages(a: ChatMessage, b: ChatMessage): number {
  const at = Date.parse(a.createdAt);
  const bt = Date.parse(b.createdAt);
  if (at !== bt) return at - bt;
  return (a.id ?? Number.MAX_SAFE_INTEGER) - (b.id ?? Number.MAX_SAFE_INTEGER);
}

/** A send this browser made and the server has not yet returned, as the thread holds it. */
export interface PendingSend {
  readonly wire: MessageResponse;
  readonly local: LocalSend;
}

/**
 * The thread as drawn: the server's rows, then every pending send the server has not returned,
 * oldest first. A pending send whose `clientId` the server already holds is the same message and is
 * dropped — the server's record wins, but its local preview rides on so a film still processing
 * keeps the frame the trainer picked.
 */
export function mergeThread(
  server: readonly MessageResponse[],
  pending: readonly PendingSend[],
  previews: ReadonlyMap<string, string>,
  myUserId: string | null,
): ChatMessage[] {
  const seen = new Set<string>();
  const seenIds = new Set<number>();
  const out: ChatMessage[] = [];
  for (const wire of server) {
    if (typeof wire.id === 'number') {
      if (seenIds.has(wire.id)) continue;
      seenIds.add(wire.id);
    }
    if (wire.clientId !== undefined) seen.add(wire.clientId);
    const preview = wire.clientId === undefined ? undefined : previews.get(wire.clientId);
    out.push(toChatMessage(wire, myUserId, null, preview ?? null));
  }
  for (const entry of pending) {
    if (entry.wire.clientId !== undefined && seen.has(entry.wire.clientId)) continue;
    const preview = entry.wire.clientId === undefined ? undefined : previews.get(entry.wire.clientId);
    out.push(toChatMessage(entry.wire, myUserId, entry.local, preview ?? null));
  }
  return out.sort(compareMessages);
}

/** The newest message the server holds, by id — what `POST …/read` names. */
export function newestServerId(messages: readonly ChatMessage[]): number | null {
  let best: number | null = null;
  for (const message of messages) {
    if (message.id !== null && (best === null || message.id > best)) best = message.id;
  }
  return best;
}

/** Whether a film or a voice note is still being processed by the server. */
export function mediaStatusOf(message: ChatMessage): 'Processing' | 'Ready' | 'Failed' | null {
  if (message.kind === 'video') return message.wire.video?.status ?? null;
  if (message.kind === 'voice') return message.wire.voice?.status ?? null;
  return null;
}
